import React from "react";
import { useGridBaseline } from "../hooks/useBaseline";

interface Props {
  baCode: string | null | undefined;
  projectDalysPerMwh?: number | null;
  projectPdfsPerMwh?: number | null;
  projectUsdPerMwh?: number | null;
  projectGenerationMwh?: number | null;
}

function formatSigFig(val: number | null | undefined, digits = 3): string {
  if (val == null) return "N/A";
  const abs = Math.abs(val);
  if (abs === 0) return "0";
  if (abs >= 1) return abs.toFixed(Math.max(0, digits - Math.floor(Math.log10(abs)) - 1));
  return abs.toPrecision(digits);
}

function formatCompact(val: number | null | undefined): string {
  if (val == null) return "N/A";
  const abs = Math.abs(val);
  if (abs >= 1e9) return `${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(abs / 1e3).toFixed(1)}K`;
  if (abs >= 1) return abs.toFixed(1);
  return abs.toPrecision(3);
}

const BaselineComparisonCard: React.FC<Props> = ({
  baCode,
  projectDalysPerMwh,
  projectPdfsPerMwh,
  projectUsdPerMwh,
  projectGenerationMwh,
}) => {
  const { data: baseline, isLoading, error } = useGridBaseline(baCode);

  if (!baCode) return null;
  if (isLoading) {
    return (
      <div className="bg-surface-800 rounded-xl p-5 border border-border animate-pulse">
        <div className="h-4 bg-surface-700 rounded w-48 mb-4" />
        <div className="space-y-3">
          <div className="h-3 bg-surface-700 rounded w-full" />
          <div className="h-3 bg-surface-700 rounded w-full" />
          <div className="h-3 bg-surface-700 rounded w-full" />
        </div>
      </div>
    );
  }
  if (error || !baseline) return null;

  const gridGenMwh = baseline.total_generation_mwh;
  const projGenMwh = projectGenerationMwh;

  const rows = [
    {
      label: "Human Health",
      unit: "DALYs",
      project: projectDalysPerMwh,
      baseline: baseline.human_health_dalys_per_mwh,
      color: "text-health-400",
    },
    {
      label: "Ecosystem Quality",
      unit: "species.yr",
      project: projectPdfsPerMwh,
      baseline: baseline.ecosystem_quality_species_yr_per_mwh,
      color: "text-biodiversity-400",
    },
    {
      label: "Economic Impact",
      unit: "USD",
      project: projectUsdPerMwh,
      baseline: baseline.resource_scarcity_usd_per_mwh,
      color: "text-economic-400",
    },
  ];

  const completenessPercent = Math.round(baseline.data_completeness * 100);

  // Dynamic label based on period_type
  const periodLabel = baseline.period_type?.startsWith("annual_")
    ? `Annual baseline (${baseline.period_type.replace("annual_", "")})`
    : "30-day rolling average";

  function annualTotal(perMwh: number | null | undefined, genMwh: number | null | undefined): number | null {
    if (perMwh == null || genMwh == null) return null;
    return Math.abs(perMwh) * genMwh;
  }

  return (
    <div className="bg-surface-800 rounded-xl p-5 border border-quantum-500/20">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-body font-semibold text-quantum-400">
            Grid Baseline Comparison
          </h3>
          <p className="text-caption text-text-muted mt-0.5">
            {periodLabel} for {baCode}
          </p>
        </div>
        <span className="text-[10px] text-text-muted bg-surface-700 px-2 py-0.5 rounded">
          {completenessPercent}% data coverage
        </span>
      </div>

      <p className="text-body-sm text-text-secondary mb-4">
        Each MWh from the {baCode} grid causes environmental damage. Your project
        creates a net benefit by displacing that generation.
      </p>

      {/* Comparison table */}
      <div className="overflow-hidden rounded-lg border border-border/50">
        <table className="w-full text-body-sm">
          <thead>
            <tr className="bg-surface-700/50">
              <th className="text-left px-3 py-2 text-text-muted font-medium" rowSpan={2}>Impact Category</th>
              <th className="text-center px-3 py-1.5 text-text-muted font-medium border-b border-border/30" colSpan={2}>
                {"\u25BC"} Grid ({baCode})
              </th>
              <th className="text-center px-3 py-1.5 text-text-muted font-medium border-b border-border/30" colSpan={2}>
                {"\u25B2"} Project
              </th>
            </tr>
            <tr className="bg-surface-700/50">
              <th className="text-right px-3 py-1 text-[10px] text-text-muted font-normal">per MWh</th>
              <th className="text-right px-3 py-1 text-[10px] text-text-muted font-normal">Annual Total</th>
              <th className="text-right px-3 py-1 text-[10px] text-text-muted font-normal">per MWh</th>
              <th className="text-right px-3 py-1 text-[10px] text-text-muted font-normal">Annual Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-border/30">
                <td className="px-3 py-2">
                  <span className={row.color}>{row.label}</span>
                  <span className="text-caption text-text-muted ml-1">({row.unit})</span>
                </td>
                <td className="text-right px-3 py-2 font-mono text-red-400">
                  {formatSigFig(row.baseline)}
                </td>
                <td className="text-right px-3 py-2 font-mono text-red-400/60 text-[11px]">
                  {formatCompact(annualTotal(row.baseline, gridGenMwh))}
                </td>
                <td className="text-right px-3 py-2 font-mono text-emerald-400">
                  {formatSigFig(row.project)}
                </td>
                <td className="text-right px-3 py-2 font-mono text-emerald-400/60 text-[11px]">
                  {formatCompact(annualTotal(row.project, projGenMwh))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4 mt-3 text-[10px] text-text-muted">
        <span className="inline-flex items-center gap-1">
          <span className="text-red-400">{"\u25BC"}</span> Harm — damage caused by grid generation
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="text-emerald-400">{"\u25B2"}</span> Benefit — damage avoided by project
        </span>
      </div>

      <p className="text-[10px] text-text-muted mt-2">
        Source: TotalView grid impact analysis, {periodLabel.toLowerCase()}.
        {gridGenMwh ? ` Grid: ${formatCompact(gridGenMwh)} MWh/yr.` : ""}
        {projGenMwh ? ` Project: ${formatCompact(projGenMwh)} MWh/yr.` : ""}
      </p>
    </div>
  );
};

export default BaselineComparisonCard;
